import { GAME_WIDTH } from '../config.js';
import { ROAD_WHEEL_Y } from './carCatalog.mjs';
import { pxPerSec } from './carPhysics.mjs';
import { createCarSprite, setCarFacing, setCarGroundY } from './CarSprite.js';

// The scripted traffic on a v2 training route: three kinds of car, each with one job.
//
//   'side'      hidden up a side road until the route rules wake it (`trigger-side`), then
//               it rolls down to the junction, waits its turn, pulls out into the near lane
//               and drives off to the right. The `stop`/`yield` tasks only pass once it has
//               cleared the junction (`passedX`).
//   'oncoming'  comes at you: starts off the LEFT edge of the camera and runs right down the
//               near lane. Hitting it is the whole point of the `oncoming` task.
//   'parked'    stands in YOUR lane (the far one) with a solid line beside it, so it can't be
//               overtaken — it only pulls away (`parked-go`) once you have waited behind it.
//
// A traffic light is furniture rather than traffic, but it lives here for the same reason:
// it is a thing the segment art doesn't contain and the director has to drive. See
// `TrafficLight` at the bottom.
//
//   const car = new TrafficActor(scene, { kind: 'side', model: 'panda', x: junctionX });
//   car.start();            // on the rule engine's trigger
//   car.update(dt, playerX, playerLane); // every frame, dt in seconds
//   car.passedX(junctionX); // has it cleared the junction?

// Where the side road disappears out of the top of the frame — the car comes down from here.
const SIDE_ROAD_FEET_Y = 470;
const SIDE_DOWN_S = 1.4;      // seconds to roll down the side road to the junction mouth
const SIDE_GO_WITHIN = 700;   // …where it waits until the player's centre is this close (px)
const SIDE_VISIBLE_MARGIN = 220; // …and the junction mouth is at least this far inside the screen's left edge
const SIDE_MAX_WAIT_S = 8;    // …or this long, so a child who parks far back never deadlocks
const SIDE_WAIT_S = 0.4;      // a last look both ways once it has decided to pull out
const SIDE_KMH = 30;          // its speed once it has pulled out — slow enough to stay in view
const SIDE_MERGE_PX = 120;    // px of travel over which it turns from the far lane (yours) into the near one
const SIDE_MOUTH_OFFSET = 60; // it waits this far LEFT of the side road's centre: a right turn starts on the near side
const SIDE_SAFE_FRONT = 240;  // it only pulls out while the player's bonnet is at least this far right of the junction…
const SIDE_PLAYER_PAST = 120; // …or the player's centre is already this far past it (then it goes behind them)
const ONCOMING_KMH = 30;
const PARKED_KMH = 10;        // its cruise once it has pulled away — slow, so it can be overtaken
const PARKED_ACCEL_S = 2.5;   // a Panda pulling away takes a moment to reach PARKED_KMH
const PARKED_DECEL_KMH_S = 12; // …and brakes at most this hard (km/h per second) — never instantly
const PARKED_SAFE_GAP = 520;  // centre-to-centre px it keeps behind a car ahead in its lane
const OFFSCREEN_PAD = 500;    // px past the camera edge before an actor is done
const CLEAR_PX = 140;         // px past the junction that counts as "through it"
const PARKED_GONE_PX = 220;   // …and how far a pulling-away car must go to stop being in the way

// The lane a car occupies, as the y its wheels stand on. Cars are ~190 px of image tall and
// the two lanes are only 40 px apart, so a plain full-height AABB would have every car in
// the far lane "hit" every car in the near one — collisions compare ground lines instead.
const laneY = (lane) => ROAD_WHEEL_Y[lane === 'left' ? 'left' : 'right'];

export class TrafficActor {
  /**
   * `kind` is 'side' | 'oncoming' | 'parked'; `x` the world x it starts at (for 'oncoming'
   * that is overridden by the camera's left edge when it starts); `lane` the lane it drives
   * in; `junctionX` the x a 'side' car has to clear (defaults to `x`).
   */
  constructor(scene, { kind = 'side', model = 'panda', x = 0, lane = 'right',
    worldWidth = 0, junctionX = null } = {}) {
    this.scene = scene;
    this.kind = kind;
    this.model = model;
    this.worldWidth = worldWidth;
    this.junctionX = junctionX ?? x;
    this.lane = kind === 'parked' ? 'left' : lane;
    this.x = x;
    this.passed = false;      // a 'side' car is through the junction
    this.gone = false;        // a 'parked' car has pulled far enough away to be out of the way
    this._t = 0;
    this._startX = x;
    // 'parked' stands there from the first frame — the child has to see it to wait behind it.
    this.state = kind === 'parked' ? 'idle' : 'hidden';

    // A side-road car sits (and comes down) a little left of the opening's centre, so that
    // when it turns right it is out of the far lane — the player's, where a car waiting at
    // the stop line has its bonnet — well before it reaches that bonnet.
    if (kind === 'side') this.x = x - SIDE_MOUTH_OFFSET;
    const groundY = kind === 'side' ? SIDE_ROAD_FEET_Y : laneY(this.lane);
    this._groundY = groundY;
    this.facing = kind === 'oncoming' || kind === 'side' ? 'right' : 'left';
    this.sprite = createCarSprite(scene, model, 'side', this.x, groundY, { wheelY: groundY });
    setCarFacing(this.sprite, this.facing);
    this.sprite.setDepth(groundY);
    this.sprite.setVisible(kind === 'parked');
  }

  get active() { return this.state !== 'hidden' && this.state !== 'done'; }

  /** Wake it up: the rule engine's `trigger-side` / `trigger-oncoming` / `parked-go`. */
  start() {
    if (!this.sprite || this.state === 'done') return;
    if (this.kind === 'side') {
      if (this.state !== 'hidden') return;
      this.state = 'down';
      this._t = 0;
      this.sprite.setVisible(true);
      return;
    }
    if (this.kind === 'oncoming') {
      if (this.state !== 'hidden') return;
      // It has to come INTO view, not pop up in front of you: just off the left edge.
      this.x = this.scene.cameras.main.scrollX - 300;
      this.sprite.x = this.x;
      this.state = 'go';
      this.sprite.setVisible(true);
      return;
    }
    if (this.state === 'idle') { this.state = 'go'; this._startX = this.x; this._t = 0; this._v = 0; }
  }

  /**
   * One frame. `playerX`/`playerLane` are the player's car centre and lane: the side-road
   * car times its pull-out by them, the pulled-away car keeps its distance behind the
   * player once overtaken.
   */
  update(dt, playerX = null, playerLane = null, playerFront = null) {
    if (!this.sprite || !this.active) return;
    if (this.kind === 'side') this._updateSide(dt, playerX, playerFront);
    else if (this.kind === 'oncoming') this._updateOncoming(dt);
    else this._updateParked(dt, playerX, playerLane);
    this.sprite.x = this.x;
  }

  _updateSide(dt, playerX, playerFront) {
    if (this.state === 'down') {
      this._t += dt;
      const k = Math.min(1, this._t / SIDE_DOWN_S);
      // Down the side road towards the camera: it grows as it comes (setCarGroundY does the
      // perspective), which is what sells "it is pulling out in front of you". It arrives on
      // the FAR lane — the player's — because a car turning right out of a side road on the
      // far side of the road has to cross that lane first. That is what makes not giving way
      // a collision rather than a formality; it merges into the near lane as it drives off.
      this._setGround(SIDE_ROAD_FEET_Y + (laneY('left') - SIDE_ROAD_FEET_Y) * k);
      if (k >= 1) { this.state = 'wait'; this._t = 0; }
      return;
    }
    if (this.state === 'wait') {
      // Sits at the mouth until the player is close enough for the pull-out to be the thing
      // they have to give way to — a car that crosses while they are still 9 m away is just
      // scenery — AND the junction is actually on the screen: what you stop for has to be
      // something you can see. Driving left, the player is at higher x than the junction.
      this._t += dt;
      const cam = this.scene.cameras.main;
      const onScreen = this.junctionX >= cam.scrollX + SIDE_VISIBLE_MARGIN
        && this.junctionX <= cam.scrollX + GAME_WIDTH;
      const near = playerX != null && playerX - this.junctionX <= SIDE_GO_WITHIN;
      // Never into a car that is standing on the junction: the gap has to be there — the
      // player's bonnet still clear of the opening, or the player already through it.
      const front = playerFront ?? (playerX == null ? null : playerX - 180);
      const gap = front == null || front >= this.junctionX + SIDE_SAFE_FRONT
        || playerX < this.junctionX - SIDE_PLAYER_PAST;
      if (gap && ((near && onScreen) || this._t >= SIDE_MAX_WAIT_S)) { this.state = 'pulling'; this._t = 0; }
      return;
    }
    if (this.state === 'pulling') {
      this._t += dt;
      if (this._t >= SIDE_WAIT_S) { this.state = 'go'; this._startX = this.x; }
      return;
    }
    this.x += pxPerSec(SIDE_KMH) * dt;
    const merge = Math.min(1, (this.x - this._startX) / SIDE_MERGE_PX);
    this._setGround(laneY('left') + (laneY(this.lane) - laneY('left')) * merge);
    if (this.x > this.junctionX + CLEAR_PX) this.passed = true;
    this._retireRight();
  }

  _updateOncoming(dt) {
    this.x += pxPerSec(ONCOMING_KMH) * dt;
    this._retireRight();
  }

  _updateParked(dt, playerX, playerLane) {
    if (this.state !== 'go') return;
    // Its speed ramps: up at PARKED_KMH / PARKED_ACCEL_S (it pulls away, it doesn't launch)
    // and down at PARKED_DECEL_KMH_S. Once the player has overtaken it and is back in its
    // lane in front, it eases off to hold PARKED_SAFE_GAP behind them — it never drives into
    // their back, but it cannot stop dead either, so a hard brake right under its nose is
    // still the player's own doing.
    const playerAhead = playerX != null && playerLane === this.lane
      && playerX < this.x && this.x - playerX < PARKED_SAFE_GAP;
    const target = playerAhead ? 0 : PARKED_KMH;
    const v = this._v ?? 0;
    this._v = v < target
      ? Math.min(target, v + (PARKED_KMH / PARKED_ACCEL_S) * dt)
      : Math.max(target, v - PARKED_DECEL_KMH_S * dt);
    this.x -= pxPerSec(this._v) * dt;
    if (this._startX - this.x >= PARKED_GONE_PX) this.gone = true;
    if (this.x < this.scene.cameras.main.scrollX - OFFSCREEN_PAD) this._retire();
  }

  _retireRight() {
    if (this.x > this.scene.cameras.main.scrollX + GAME_WIDTH + OFFSCREEN_PAD) this._retire();
  }

  _retire() {
    this.state = 'done';
    this.sprite?.setVisible(false);
  }

  _setGround(y) {
    this._groundY = y;
    setCarGroundY(this.sprite, y);
    this.sprite.setDepth(y);
  }

  /** Has it driven clear of the world x `x` (a junction it had to get through)? */
  passedX(x) {
    return this.x > x + CLEAR_PX;
  }

  /** Is it ahead of a car at `x` driving left, and close enough to be in the way? */
  aheadOf(x, within = 500) {
    const gap = x - this.x;
    return gap > 0 && gap < within;
  }

  /**
   * The footprint to test a collision against: the body's x span plus the ground line its
   * wheels stand on. `null` when there is nothing on the road to hit.
   */
  bounds() {
    if (!this.sprite || !this.active || !this.sprite.visible) return null;
    const w = (this.sprite.img?.displayWidth ?? 200) * 0.9;
    return { left: this.x - w / 2, right: this.x + w / 2, groundY: this._groundY };
  }

  destroy() {
    this.sprite?.destroy();
    this.sprite = null;
    this.state = 'done';
  }
}

// --- the traffic light ------------------------------------------------------------------

const LIGHT_H = 150;                       // art height, when the art exists
const LIGHT_BOX = { w: 40, h: 120, y: 430 };   // …and the drawn fallback's post
const LIGHT_BULB_R = 14;
const LIGHT_KEYS = {
  red: 'traffic_light_red', redyellow: 'traffic_light_redyellow', green: 'traffic_light_green',
};
const LIGHT_COLOURS = {
  off: 0x333333, red: 0xd23b3b, redyellow: 0xe8a13a, green: 0x3fa84a,
};
const LIGHT_DEPTH = 5;   // beside the road, behind the cars (which sort by their wheel y)

/**
 * The three-state signal at a `lights` segment. Starts 'off' (nothing is asked of you yet);
 * the director runs it red → red+yellow → green once the rules trigger it.
 */
export class TrafficLight {
  constructor(scene, { x = 0 } = {}) {
    this.scene = scene;
    this.state = 'off';
    if (scene.textures.exists(LIGHT_KEYS.red)) {
      const img = scene.add.image(x, LIGHT_BOX.y, LIGHT_KEYS.red);
      img.setDisplaySize(LIGHT_H * (img.width / img.height || 0.4), LIGHT_H);
      // The art is drawn with the lamps facing right; the car comes from the right, so the
      // signal is mirrored to face it (setTexture on a state change keeps the flip).
      img.setFlipX(true);
      img.setDepth(LIGHT_DEPTH);
      this._img = img;
      this._bulb = null;
    } else {
      // No art yet: a dark post with one lit lamp on it, which is all the rule needs to read.
      this._img = scene.add.rectangle(x, LIGHT_BOX.y, LIGHT_BOX.w, LIGHT_BOX.h, 0x2b2b31)
        .setStrokeStyle(3, 0x14141a).setDepth(LIGHT_DEPTH);
      this._bulb = scene.add.circle(x, LIGHT_BOX.y - LIGHT_BOX.h / 2 + 26, LIGHT_BULB_R,
        LIGHT_COLOURS.off).setDepth(LIGHT_DEPTH + 1);
    }
    this.set('off');
  }

  set(state) {
    this.state = LIGHT_COLOURS[state] === undefined ? 'off' : state;
    if (this._bulb) { this._bulb.setFillStyle(LIGHT_COLOURS[this.state]); return; }
    const key = LIGHT_KEYS[this.state];
    if (!key || !this.scene.textures.exists(key)) {
      // 'off' has no art of its own — the unlit signal is simply the red one, dimmed.
      this._img.setAlpha(this.state === 'off' ? 0.45 : 1);
      return;
    }
    this._img.setAlpha(1);
    this._img.setTexture(key);
    // setTexture resets the display size to the new frame's natural one — re-fit it.
    this._img.setDisplaySize(LIGHT_H * (this._img.width / this._img.height || 0.4), LIGHT_H);
  }

  destroy() {
    this._img?.destroy();
    this._bulb?.destroy();
    this._img = null;
    this._bulb = null;
  }
}
